import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import {first, get, groupBy, last, memoize, omit} from "lodash";
import moment from "moment";
import {USG_STATE} from "../constans/eventTypes";
import {ParamTypes} from "../constans/paramTypes";
import {SectorType} from "../constans/sectorTypes";
import {getParturitionIndexForASow} from "./AnimalDocumentsUtils";
import {
    REMOVE_TECHNOLOGY_GROUP_REASONS,
    formatTechnoGroupName,
    getTechnologyGroupByTime,
} from "./TechnologyGroupUtils";
import AnimalTypes from "@wesstron/utils/Api/constants/animalTypes";
import sowState from "../constans/sowState";
import Flatbush from "flatbush";
import {ParamGroups} from "../constans/paramGroups";
import {getAnimalTimespanInGroupByAllEvents} from "./GroupUtils";

function getDateParams(event, sharedParameters) {
    if (!event && !sharedParameters?.dataEv) return {};
    const date = sharedParameters?.dataEv
        ? moment(sharedParameters?.dataEv)
        : moment(event.EvTime);
    return {
        day: date.isoWeekday(),
        month: date.month(),
        week: date.isoWeek(),
        year: date.year(),
    };
}

export function getGroupDay(event, group) {
    if (!event) return undefined;
    const startTimeInFarmTime = moment(
        moment(group.start).format(moment.HTML5_FMT.DATE)
    );
    return moment(event.EvTime)
        .startOf("day")
        .diff(startTimeInFarmTime, "days");
}

function getGroupParams(event, sharedParameters) {
    const obj = {};
    if (sharedParameters?.group_nr) {
        obj.group_nr = sharedParameters.group_nr;
    } else if (sharedParameters?.temporaryParams?.getGroup) {
        let group = sharedParameters.temporaryParams.getGroup(event);
        obj.group_nr = group.name;
        obj.groupStart = group.start;
        obj.groupDay = getGroupDay(event, group);
    }
    return obj;
}

export function createParamObject(
    key,
    value,
    sharedParameters,
    event,
    type,
    category
) {
    return {
        ...omit(sharedParameters, ["temporaryParams"]),
        key,
        value,
        type,
        user: event ? event.EvData.OperID || event.OperID : undefined,
        EvID: event?.EvID,
        category,
        dataEv: sharedParameters?.dataEv
            ? sharedParameters.dataEv
            : event
              ? event.EvTime
              : sharedParameters?.temporaryParams?.groupStart,
        cycleDay:
            sharedParameters?.cycleDay !== undefined
                ? sharedParameters.cycleDay
                : sharedParameters?.temporaryParams?.getCycleDay
                  ? sharedParameters.temporaryParams.getCycleDay(event)
                  : undefined,
        cycleWeek:
            sharedParameters?.cycleWeek !== undefined
                ? sharedParameters.cycleWeek
                : sharedParameters?.temporaryParams?.getCycleWeek
                  ? sharedParameters.temporaryParams.getCycleWeek(event)
                  : undefined,
        PlcmntID:
            sharedParameters?.PlcmntID !== undefined
                ? sharedParameters.PlcmntID
                : sharedParameters?.temporaryParams?.getPlcmntID &&
                  sharedParameters.temporaryParams.getPlcmntID(event),
        DictionaryID:
            sharedParameters?.DictionaryID !== undefined
                ? sharedParameters.DictionaryID
                : sharedParameters?.temporaryParams?.getDictionaryID &&
                  sharedParameters.temporaryParams.getDictionaryID(event),
        ...getGroupParams(event, sharedParameters),
        ...getDateParams(event, sharedParameters),
    };
}

function giltWasInGroup(groups, AnmID, events, EvTime) {
    const groupWithAnimal = groups.find((item) => {
        if (!item.AnmIDs.includes(AnmID) && !item.Rmvd.includes(AnmID))
            return false;
        const timeSpans = getAnimalTimespanInGroupByAllEvents(
            events,
            item.AnmGrp
        )[AnmID];
        if (!timeSpans) return true;
        for (let timeSpan of timeSpans) {
            if (EvTime >= timeSpan[0] && EvTime <= timeSpan[1]) return true;
        }
        return false;
    });
    return !!groupWithAnimal;
}

function getTechnologyGroupNumber(
    cycle,
    {technologyGroupStart, technologyGroupWeeks}
) {
    const lastInsemination = first(cycle[EventTypes.INSEMINATION]);
    if (!lastInsemination) return [];
    const inseminationTime = moment.utc(lastInsemination.EvTime);
    const {start, end} = getTechnologyGroupByTime(
        inseminationTime,
        technologyGroupStart,
        technologyGroupWeeks
    );
    let groupRow = {
        name: formatTechnoGroupName(start, end, true),
        start: +start,
        from: +inseminationTime,
        to: Infinity,
    };
    let groups = [];
    for (let isMommyEvent of cycle[EventTypes.IS_MOMMY]) {
        groupRow.to = isMommyEvent.EvTime;
        groups.push({...groupRow}); // usuniecie referencji
        const groupForEvent = getTechnologyGroupByTime(
            isMommyEvent.EvData.GroupStart,
            technologyGroupStart,
            technologyGroupWeeks
        );
        groupRow = {
            name: formatTechnoGroupName(
                groupForEvent.start,
                groupForEvent.end,
                true
            ),
            start: +groupForEvent.start,
            from: isMommyEvent.EvTime,
            to: Infinity,
        };
    }
    groups.push(groupRow);
    return groups;
}

function calculateParametersRelatedToParturition(
    cycle,
    sharedParameters,
    utilResults,
    temporaryAnimalData = {},
    isLast,
    crossBreedingDictionary,
    pedigreeDictionary
) {
    if (!temporaryAnimalData.parturition) {
        temporaryAnimalData.parturition = {
            amount: 0,
            weight: 0,
            parturitions: 0,
        };
    }
    const parturition = cycle[EventTypes.PARTURITION][0];
    if (!parturition) return [];
    temporaryAnimalData.parturition.parturitions++;
    temporaryAnimalData.parturition.amount +=
        parturition.EvData.HealthyCnt || 0;
    const params = [
        // "wszystkie to jest suma zywe martwe mumie, slabe i loszki wchodza w sklad tych trzech" - Daniel 17.01.24
        // wszystkie prosiaki
        createParamObject(
            "bornPiglet",
            (parturition.EvData.HealthyCnt || 0) +
                (parturition.EvData.DeadCnt ||
                    0) /*+ (parturition.EvData.WeakCnt || 0)*/ +
                (parturition.EvData.MummyCnt ||
                    0) /*+ (parturition.EvData.GiltsCnt || 0)*/,
            sharedParameters,
            parturition,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        ),
        // prosiaki z HealthyCnt
        createParamObject(
            "liveBornPiglet",
            parturition.EvData.HealthyCnt || 0,
            sharedParameters,
            parturition,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        ),
        // prosiaki z DeadCnt
        createParamObject(
            "deadBornPiglet",
            parturition.EvData.DeadCnt || 0,
            sharedParameters,
            parturition,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        ),
        // prosiaki z MummyCnt
        createParamObject(
            "mummyBornPiglet",
            parturition.EvData.MummyCnt || 0,
            sharedParameters,
            parturition,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        ),
        // prosiaki z GiltsCnt
        createParamObject(
            "giltsBornPiglet",
            parturition.EvData.GiltsCnt || 0,
            sharedParameters,
            parturition,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        ),
        // prosiaki z WeakCnt
        createParamObject(
            "weakBornPiglet",
            parturition.EvData.WeakCnt || 0,
            sharedParameters,
            parturition,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        ),
    ];
    if (parturition.EvData.Weight) {
        temporaryAnimalData.parturition.weight +=
            (parturition.EvData.HealthyCnt || 0) * parturition.EvData.Weight;
        // wstawianie dla starych danych
        params.push(
            createParamObject(
                "bornPigletWeight",
                parturition.EvData.Weight,
                sharedParameters,
                parturition,
                ParamTypes.WEIGHT,
                ParamGroups.FARROWING
            )
        );
    }
    if (parturition.EvData.hasOwnProperty("Type")) {
        // jezeli znaleziono typ to dodaj parametr
        params.push(
            createParamObject(
                "levelParturition",
                parturition.EvData.Type,
                sharedParameters,
                parturition,
                ParamTypes.PARTURITION_LEVEL,
                ParamGroups.FARROWING
            )
        );
    }
    const insemination = first(cycle[EventTypes.INSEMINATION]);
    const exact = moment(parturition.EvTime);
    if (insemination) {
        // roznica dni miedzy oczekiwanym a rzeczywistym porodem
        const expected = moment
            .utc(insemination.EvTime)
            .add(utilResults.TimeFromInseminationToParturition - 1, "days");
        const exact = moment(parturition.EvTime);
        const diff = exact.diff(expected, "days");
        params.push(
            createParamObject(
                "shiftFarrowDay",
                diff,
                sharedParameters,
                parturition,
                ParamTypes.DAYS,
                ParamGroups.FARROWING
            )
        );
        const gestationDays = exact.diff(insemination.EvTime, "days");
        params.push(
            createParamObject(
                "gestationDays",
                gestationDays,
                sharedParameters,
                parturition,
                ParamTypes.DAYS,
                ParamGroups.FARROWING
            )
        );
        const sow = pedigreeDictionary?.sows?.[insemination.AnmID];
        const boar = pedigreeDictionary?.boars?.[insemination.EvData.BoarID];
        if (sow?.Race && boar?.Race) {
            const row = crossBreedingDictionary?.WData?.find(
                (item) =>
                    item.BoarRace === boar.Race && item.SowRace === sow.Race
            );
            if (row) {
                params.push(
                    createParamObject(
                        "pigletRace",
                        row.Value,
                        sharedParameters,
                        parturition,
                        ParamTypes.RACE,
                        ParamGroups.FARROWING
                    )
                );
            }
        }
    }
    if (cycle[EventTypes.SEPARATION].length === 0) {
        if (isLast) {
            const expectedSeparation = moment(parturition.EvTime)
                .add(utilResults.TimeOnBirthRoom, "days")
                .startOf("day");
            const diff = expectedSeparation.diff(
                moment.utc().startOf("day"),
                "days"
            );
            params.push(
                createParamObject(
                    "expectedWeaning",
                    diff,
                    sharedParameters,
                    parturition,
                    ParamTypes.DAYS,
                    ParamGroups.SEPARATION
                )
            );
        }
        const diffToToday = moment().startOf("day").diff(exact, "days");
        params.push(
            createParamObject(
                "dayAfterFarrow",
                diffToToday,
                sharedParameters,
                parturition,
                ParamTypes.DAYS,
                ParamGroups.FARROWING
            )
        );
    } else {
        const separation = cycle[EventTypes.SEPARATION][0];
        const diff = moment(separation.EvTime).diff(exact, "days");
        params.push(
            createParamObject(
                "dayAfterFarrow",
                diff,
                sharedParameters,
                parturition,
                ParamTypes.DAYS,
                ParamGroups.FARROWING
            )
        );
    }
    return params;
}

function calculateParametersRelatedToFallPiglets(cycle, sharedParameters) {
    const params = [];
    const parturition = cycle[EventTypes.PARTURITION][0];
    let sum = 0;
    for (let fallPiglets of cycle[EventTypes.FALL_PIGLETS]) {
        sum += fallPiglets.EvData.Piglets;
        // dla kazdego zgloszenia dodaj ilosc
        params.push(
            createParamObject(
                "deadPiglet",
                fallPiglets.EvData.Piglets,
                sharedParameters,
                fallPiglets,
                ParamTypes.AMOUNT,
                ParamGroups.FARROWING
            )
        );
        if (fallPiglets.EvData.Weight) {
            params.push(
                createParamObject(
                    "deadPigletWeight",
                    fallPiglets.EvData.Weight,
                    sharedParameters,
                    fallPiglets,
                    ParamTypes.WEIGHT,
                    ParamGroups.FARROWING
                )
            );
        }
        if (parturition) {
            // oblicz roznice w dniach dla wieku upadku
            const diff = moment(fallPiglets.EvTime)
                .startOf("day")
                .diff(moment(parturition.EvTime).startOf("day"), "days");
            for (let i = 0; i < fallPiglets.EvData.Piglets; i++) {
                params.push(
                    createParamObject(
                        "deadPigletAge",
                        diff,
                        sharedParameters,
                        fallPiglets,
                        ParamTypes.DAYS,
                        ParamGroups.FARROWING
                    )
                );
            }
        }
    }
    params.push(
        createParamObject(
            "deadPigletInCycle",
            sum,
            sharedParameters,
            null,
            ParamTypes.AMOUNT,
            ParamGroups.FARROWING
        )
    );
    return params;
}

function calculateParametersRelatedToSeparation(
    cycle,
    sharedParameters,
    buildingsMap,
    temporaryAnimalData,
    isMommy
) {
    if (!temporaryAnimalData.separation) {
        temporaryAnimalData.separation = {
            weight: 0,
            amount: 0,
            parturitions: 0,
        };
    }
    if (cycle[EventTypes.SEPARATION].length === 0) return [];
    const params = [];
    const parturition = cycle[EventTypes.PARTURITION][0];
    let separatedCount = 0;
    const dayGroups = {};
    for (let separation of cycle[EventTypes.SEPARATION]) {
        separatedCount += separation.EvData.PiCnt;
        // ilosc odsadzonych prosiat
        // params.push(createParamObject("weanedPiglet", separation.EvData.PiCnt, sharedParameters, separation, ParamTypes.AMOUNT));
        // waga odsadzonych prosiat
        // params.push(createParamObject("weanedPigletWeight", separation.EvData.PiWeight, sharedParameters, separation, ParamTypes.WEIGHT));
        // sektor odsadzenia
        const destination = buildingsMap.get(separation.EvData.DstID);
        if (destination) {
            const day = moment(separation.EvTime).format(moment.HTML5_FMT.DATE);
            const key = `${day}_${destination.SType}`;
            if (!dayGroups[key])
                dayGroups[key] = {
                    weanedPiglet: 0,
                    weanedPigletWeight: [],
                    separation,
                    destination: destination.SType,
                    dstID: separation.EvData.DstID,
                };
            dayGroups[key].weanedPiglet += separation.EvData.PiCnt;
            dayGroups[key].weanedPigletWeight.push({
                amount: separation.EvData.PiCnt,
                weight: separation.EvData.PiWeight,
            });
        }
    }
    const keys = Object.keys(dayGroups);
    let allSeparationsWeight = 0;
    let allSeparationsAmount = 0;
    for (let i = 0; i < keys.length; i++) {
        const day = keys[i];
        // przyjmowany jest pierwszy odsad znaleziony dla dnia
        params.push(
            createParamObject(
                "weanedPiglet",
                dayGroups[day].weanedPiglet,
                sharedParameters,
                dayGroups[day].separation,
                ParamTypes.AMOUNT,
                ParamGroups.SEPARATION
            )
        );
        params.push(
            createParamObject(
                "weanedSector",
                dayGroups[day].destination,
                sharedParameters,
                dayGroups[day].separation,
                ParamTypes.SECTOR_TYPE,
                ParamGroups.SEPARATION
            )
        );
        params.push(
            createParamObject(
                "weanedLocation",
                dayGroups[day].dstID,
                sharedParameters,
                dayGroups[day].separation,
                ParamTypes.LOCATION,
                ParamGroups.SEPARATION
            )
        );
        let sumWeight = 0;
        let sumAmount = 0;
        for (let row of dayGroups[day].weanedPigletWeight) {
            sumWeight += row.amount * row.weight;
            sumAmount += row.amount;
        }
        if (dayGroups[day].destination === SectorType.PIGLET_HOUSE) {
            params.push(
                createParamObject(
                    "weanedToPigletHouse",
                    dayGroups[day].weanedPiglet,
                    sharedParameters,
                    dayGroups[day].separation,
                    ParamTypes.AMOUNT,
                    ParamGroups.SEPARATION
                )
            );
            let sumWeight = 0;
            let sumAmount = 0;
            for (let row of dayGroups[day].weanedPigletWeight) {
                sumWeight += row.amount * row.weight;
                sumAmount += row.amount;
            }
            allSeparationsWeight += sumWeight;
            allSeparationsAmount += sumAmount;
            params.push(
                createParamObject(
                    "weanedPigletWeight",
                    Math.round(sumWeight / sumAmount),
                    sharedParameters,
                    dayGroups[day].separation,
                    ParamTypes.WEIGHT,
                    ParamGroups.SEPARATION
                )
            );
        } else if (dayGroups[day].destination === SectorType.BABY_ROOM) {
            params.push(
                createParamObject(
                    "weanedPigletBabyRoomWeight",
                    Math.round(sumWeight / sumAmount),
                    sharedParameters,
                    dayGroups[day].separation,
                    ParamTypes.WEIGHT,
                    ParamGroups.SEPARATION
                )
            );
        } else if (dayGroups[day].destination === SectorType.BABY_ROOM) {
            params.push(
                createParamObject(
                    "weanedPigletBabyRoomWeight",
                    Math.round(sumWeight / sumAmount),
                    sharedParameters,
                    dayGroups[day].separation,
                    ParamTypes.WEIGHT
                )
            );
        }
        if (parturition) {
            // wiek odsadzonych prosiat
            const diff = moment(dayGroups[day].separation.EvTime)
                .startOf("day")
                .diff(moment(parturition.EvTime).startOf("day"), "days");
            params.push(
                createParamObject(
                    "weanedPigletAge",
                    diff,
                    sharedParameters,
                    dayGroups[day].separation,
                    ParamTypes.AGE,
                    ParamGroups.SEPARATION
                )
            );
        }
    }
    temporaryAnimalData.separation.weight += allSeparationsWeight;
    temporaryAnimalData.separation.amount += allSeparationsAmount;
    if (allSeparationsAmount > 0) {
        params.push(
            createParamObject(
                "weanedPigletWeightPerCycle",
                Math.round(allSeparationsWeight / allSeparationsAmount),
                sharedParameters,
                null,
                ParamTypes.WEIGHT,
                ParamGroups.SEPARATION
            )
        );
    }
    params.push(
        createParamObject(
            "weanedPerCycleFarrow",
            separatedCount,
            sharedParameters,
            null,
            ParamTypes.AMOUNT,
            ParamGroups.SEPARATION
        )
    );
    if (isMommy) {
        params.push(
            createParamObject(
                "weanedPerCycleSeparation",
                separatedCount / 2,
                sharedParameters,
                null,
                ParamTypes.AMOUNT,
                ParamGroups.SEPARATION
            )
        );
    } else {
        params.push(
            createParamObject(
                "weanedPerCycleSeparation",
                separatedCount,
                sharedParameters,
                null,
                ParamTypes.AMOUNT,
                ParamGroups.SEPARATION
            )
        );
    }
    return params;
}

function calculateParametersRelatedToCastration(
    cycle,
    sharedParameters,
    temporaryAnimalData
) {
    const params = [];
    const parturition = cycle[EventTypes.PARTURITION][0];
    for (let castration of cycle[EventTypes.CASTRATION]) {
        if (castration.EvData.Weighting) {
            // zglaszanie wagi porodowej
            params.push(
                createParamObject(
                    "bornPigletWeight",
                    castration.EvData.Weight,
                    sharedParameters,
                    castration,
                    ParamTypes.WEIGHT,
                    ParamGroups.FARROWING
                )
            );
            if (parturition) {
                temporaryAnimalData.parturition.weight +=
                    (parturition.EvData.HealthyCnt || 0) *
                    castration.EvData.Weight;
            }
        }
    }
    return params;
}

function calculateParametersRelatedToActiveNipples(cycle, sharedParameters) {
    const lastActiveNipples = last(cycle[EventTypes.ACTIVE_NIPPLES]);
    if (!lastActiveNipples) return [];
    return [
        // ilosc aktywnych sutkow, jezeli byla zgloszona w cyklu
        createParamObject(
            "activeNipples",
            lastActiveNipples.EvData.Nipples,
            sharedParameters,
            lastActiveNipples,
            ParamTypes.AMOUNT,
            ParamGroups.GENERAL
        ),
    ];
}

function calculateParametersRelatedToInsemination(
    cycle,
    sharedParameters,
    animal
) {
    const params = [];
    for (let insemination of cycle[EventTypes.INSEMINATION]) {
        // knury w inseminacjach
        params.push(
            createParamObject(
                "boar",
                insemination.EvData.BoarID,
                sharedParameters,
                insemination,
                ParamTypes.ANIMAL,
                ParamGroups.REPRODUCTION
            )
        );
    }
    const firstInsemination = first(cycle[EventTypes.INSEMINATION]);
    if (firstInsemination) {
        const exact = moment.utc(firstInsemination.EvTime);
        if (cycle[EventTypes.PARTURITION].length === 0) {
            if (sharedParameters.actCycle) {
                const diff = moment(animal.DtaDthTime).diff(exact, "days");
                params.push(
                    createParamObject(
                        "dayAfterInsemination",
                        diff,
                        sharedParameters,
                        firstInsemination,
                        ParamTypes.DAYS,
                        ParamGroups.REPRODUCTION
                    )
                );
            }
        } else {
            const parturition = cycle[EventTypes.PARTURITION][0];
            const diff = moment(parturition.EvTime).diff(exact, "days");
            params.push(
                createParamObject(
                    "dayAfterInsemination",
                    diff,
                    sharedParameters,
                    firstInsemination,
                    ParamTypes.DAYS,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    return params;
}

function calculateLastCycleParametersRelatedToInsemination(
    cycleTable,
    sharedParameters,
    utilResults
) {
    const params = [];
    //Maciory po planowanym terminie porodu
    const lastCycle = last(cycleTable);
    const firstInsemination = first(lastCycle[EventTypes.INSEMINATION]);
    if (firstInsemination && lastCycle[EventTypes.PARTURITION].length === 0) {
        // dni do oczekiwanego porodu
        const expectedFarrowingDate = moment
            .utc(firstInsemination.EvTime)
            .startOf("day")
            .add(utilResults.TimeFromInseminationToParturition - 1, "days");
        const diff = expectedFarrowingDate.diff(
            moment.utc().startOf("day"),
            "days"
        );
        const USG = get(
            last(lastCycle[EventTypes.USG]),
            "EvData.Pregnant",
            undefined
        );
        const abortion = lastCycle[EventTypes.NO_PREGNANCY];
        if (USG !== 0 && !abortion.length)
            //Jezeli miała USG 0 lub poronienie nie może mieć oczekiwanego porodu
            params.push(
                createParamObject(
                    "expectedFarrowing",
                    diff,
                    {...sharedParameters, dataEv: +expectedFarrowingDate},
                    firstInsemination,
                    ParamTypes.DAYS,
                    ParamGroups.REPRODUCTION
                )
            );
    }
    return params;
}

function calculateParametersRelatedToFallOrSellOfAnimal(
    animal,
    events,
    sharedParameters,
    groups
) {
    if (!animal.DtaDthTime || !events) return [];

    const params = [];
    if (animal.DthRsn === 0) {
        // powod upadku zwierzecia
        const fall = events.find(
            (item) => item.EvCode === EventTypes.FALL && !item.DtaDelTime
        );
        if (fall) {
            if (animal.AnimalKind === AnimalTypes.RENOVATION_SOW) {
                if (
                    !giltWasInGroup(groups, animal.AnmID, events, fall.EvTime)
                ) {
                    // odtwarzane sa parametry jak dla grupy, bo nie byla w zadnej
                    if (fall.EvData.Weight) {
                        params.push(
                            createParamObject(
                                "giltPigDeathWeight",
                                fall.EvData.Weight,
                                sharedParameters,
                                fall,
                                ParamTypes.WEIGHT,
                                ParamGroups.GILTS
                            )
                        );
                    }
                    if (animal.DtaBrthTime) {
                        params.push(
                            createParamObject(
                                "giltPigDeathAge",
                                moment(fall.EvTime)
                                    .startOf("day")
                                    .diff(
                                        moment(animal.DtaBrthTime).startOf(
                                            "day"
                                        ),
                                        "days"
                                    ),
                                sharedParameters,
                                fall,
                                ParamTypes.AGE,
                                ParamGroups.GILTS
                            )
                        );
                    }
                    params.push(
                        createParamObject(
                            "giltPigDeathCnt",
                            fall.EvData.AnmCnt,
                            sharedParameters,
                            fall,
                            ParamTypes.AMOUNT,
                            ParamGroups.GILTS
                        )
                    );
                    params.push(
                        createParamObject(
                            "giltPigDeathReason",
                            fall.EvData.Reasn,
                            sharedParameters,
                            fall,
                            ParamTypes.REASON,
                            ParamGroups.GILTS
                        )
                    );
                }
            } else {
                params.push(
                    createParamObject(
                        "fallReason",
                        fall.EvData.Reasn,
                        sharedParameters,
                        fall,
                        ParamTypes.REASON,
                        ParamGroups.GENERAL
                    )
                );
            }
        }
    } else {
        // wiek w dniu sprzedazy
        const sell = events.find(
            (item) => item.EvCode === EventTypes.SELL && !item.DtaDelTime
        );
        if (sell) {
            const diff = moment(sell.EvTime)
                .startOf("day")
                .diff(moment(animal.DtaBrthTime).startOf("day"), "days");
            if (animal.AnimalKind === AnimalTypes.RENOVATION_SOW) {
                if (
                    !giltWasInGroup(groups, animal.AnmID, events, sell.EvTime)
                ) {
                    // odtwarzane sa parametry jak dla grupy, bo nie byla w zadnej
                    // addWeight("giltPigSellWeight");
                    // addAge("giltPigSellAge");
                    params.push(
                        createParamObject(
                            "giltPigSellCnt",
                            sell.AnmCnt,
                            sharedParameters,
                            sell,
                            ParamTypes.AMOUNT,
                            ParamGroups.GILTS
                        )
                    );
                    if (sell.EvData.Weight) {
                        params.push(
                            createParamObject(
                                "giltPigSellWeight",
                                sell.EvData.Weight,
                                sharedParameters,
                                sell,
                                ParamTypes.WEIGHT,
                                ParamGroups.GILTS
                            )
                        );
                        params.push(
                            createParamObject(
                                "giltPigSellWeightSum",
                                sell.EvData.Weight * sell.AnmCnt,
                                sharedParameters,
                                sell,
                                ParamTypes.WEIGHT,
                                ParamGroups.GILTS
                            )
                        );
                    }
                    params.push(
                        createParamObject(
                            "giltPigSellAge",
                            diff,
                            sharedParameters,
                            sell,
                            ParamTypes.AGE,
                            ParamGroups.GILTS
                        )
                    );
                }
            } else {
                params.push(
                    createParamObject(
                        "sellAge",
                        diff,
                        sharedParameters,
                        sell,
                        ParamTypes.AGE,
                        ParamGroups.GENERAL
                    )
                );
                params.push(
                    createParamObject(
                        "sellWeight",
                        sell.EvData.Weight,
                        sharedParameters,
                        sell,
                        ParamTypes.WEIGHT,
                        ParamGroups.GENERAL
                    )
                );
            }
        }
    }
    return params;
}

function calculateParametersRelatedToUSG(cycle, sharedParameters) {
    const params = [];
    for (let usg of cycle[EventTypes.USG]) {
        // wynik badania USG
        params.push(
            createParamObject(
                "usgResult",
                usg.EvData.Pregnant,
                sharedParameters,
                usg,
                ParamTypes.RESULT,
                ParamGroups.REPRODUCTION
            )
        );
        const lastInsemination = last(cycle[EventTypes.INSEMINATION]);
        if (lastInsemination) {
            // ilosc dniu od inseminacji do badania usg
            const diff = moment(usg.EvTime)
                .startOf("day")
                .diff(
                    moment.utc(lastInsemination.EvTime).startOf("day"),
                    "days"
                );
            params.push(
                createParamObject(
                    "usgDays",
                    diff,
                    sharedParameters,
                    usg,
                    ParamTypes.DAYS,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    return params;
}

function calculateParametersRelatedToAnimalAge(animal, sharedParameters) {
    const params = [];
    if (animal.DtaBrthTime) {
        // wiek zwierzecia
        const age = moment().startOf("day").diff(animal.DtaBrthTime, "days");
        params.push(
            createParamObject(
                "age",
                age,
                sharedParameters,
                null,
                ParamTypes.AGE,
                ParamGroups.GENERAL
            )
        );
    }
    return params;
}

function getLastEvent(cycle) {
    const events = [];
    for (let key in cycle) {
        if (Array.isArray(cycle[key])) {
            events.push(...cycle[key]);
        }
    }
    if (events.length === 0) return null;
    events.sort((a, b) => b.EvTime - a.EvTime);
    return events[0];
}

function calculateParametersRelatedToRepetition(cycleTable, cyclesParams) {
    const params = [];
    const groupedByCycle = Object.values(groupBy(cycleTable, "cycle"));
    for (let repetitions of groupedByCycle) {
        if (repetitions.length > 1) {
            const lastRepetition = last(repetitions);
            const indexOfLast = cycleTable.indexOf(lastRepetition);
            params.push(
                createParamObject(
                    "repetition",
                    repetitions.length - 1,
                    cyclesParams[indexOfLast],
                    null,
                    ParamTypes.DAYS,
                    ParamGroups.GENERAL
                )
            );
            for (let i = 0; i < repetitions.length - 1; i++) {
                const indexOfCurr = cycleTable.indexOf(repetitions[i]);
                for (let insemination of repetitions[i][
                    EventTypes.INSEMINATION
                ]) {
                    // mozliwe ze tu powinno byc indexOfCurr + 1, poniewaz zaliczamy powtorke do kolejnego cyklu
                    params.push(
                        createParamObject(
                            "isRepetition",
                            i + 1,
                            cyclesParams[indexOfCurr],
                            insemination,
                            ParamTypes.AMOUNT,
                            ParamGroups.GENERAL
                        )
                    );
                }
            }
            for (let i = 1; i < repetitions.length; i++) {
                const prev = repetitions[i - 1];
                const curr = repetitions[i];
                // ostatni event w cyklu
                const endOfPrev = first(prev[EventTypes.INSEMINATION])?.EvTime;
                // pierwsza inseminacja w cyklu
                const startOfCurr = first(
                    curr[EventTypes.INSEMINATION]
                )?.EvTime;
                if (startOfCurr && endOfPrev) {
                    // roznica dni miedzy cyklami
                    const indexOfPrev = cycleTable.indexOf(prev);
                    const diff = moment
                        .utc(startOfCurr)
                        .startOf("day")
                        .diff(moment.utc(endOfPrev).startOf("day"), "days");
                    params.push(
                        createParamObject(
                            "repetitionDays",
                            diff,
                            cyclesParams[indexOfPrev],
                            first(curr[EventTypes.INSEMINATION]),
                            ParamTypes.DAYS,
                            ParamGroups.GENERAL
                        )
                    );
                }
            }
        }
    }
    return params;
}

function calculateTechnologyGroupRemoval(
    currCycle,
    nextCycle,
    index,
    cycleParams,
    events,
    utilResults,
    temporaryAnimalData = {}
) {
    if (
        currCycle[EventTypes.SEPARATION].length > 0 &&
        temporaryAnimalData.zeroPigletTimeInCycles?.[index]
    ) {
        return [
            createParamObject(
                "technologyGroupRemoval",
                REMOVE_TECHNOLOGY_GROUP_REASONS.SEPARATION,
                cycleParams[index],
                last(currCycle[EventTypes.SEPARATION]),
                ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                ParamGroups.GENERAL
            ),
        ];
    }
    const negativeUSG = currCycle[EventTypes.USG].find(
        (o) =>
            o.EvData.Pregnant === USG_STATE.NEGATIVE ||
            o.EvData.Pregnant === false
    );
    if (negativeUSG) {
        return [
            createParamObject(
                "technologyGroupRemoval",
                REMOVE_TECHNOLOGY_GROUP_REASONS.NEGATIVE_USG,
                cycleParams[index],
                negativeUSG,
                ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                ParamGroups.GENERAL
            ),
        ];
    }
    if (currCycle[EventTypes.NO_PREGNANCY].length > 0) {
        const event = currCycle[EventTypes.NO_PREGNANCY][0];
        return [
            createParamObject(
                "technologyGroupRemoval",
                `${REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PREGNANCY}_NOPREGNANCY_${event.EvData.Reason}`,
                cycleParams[index],
                currCycle[EventTypes.NO_PREGNANCY][0],
                ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                ParamGroups.GENERAL
            ),
        ];
    }
    if (!nextCycle) {
        for (let i = events.length - 1; i >= 0; i--) {
            let event = events[i];
            if (event.EvCode === EventTypes.FALL) {
                return [
                    createParamObject(
                        "technologyGroupRemoval",
                        `${REMOVE_TECHNOLOGY_GROUP_REASONS.FALL}_FALL_${event.EvData.Reasn}`,
                        cycleParams[index],
                        event,
                        ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                        ParamGroups.GENERAL
                    ),
                ];
            } else if (event.EvCode === EventTypes.SELL) {
                return [
                    createParamObject(
                        "technologyGroupRemoval",
                        REMOVE_TECHNOLOGY_GROUP_REASONS.SELL,
                        cycleParams[index],
                        event,
                        ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                        ParamGroups.GENERAL
                    ),
                ];
            }
        }
    } else if (
        currCycle.cycle === nextCycle.cycle &&
        cycleParams[index]?.temporaryParams?.getGroup &&
        cycleParams[index + 1]?.temporaryParams?.getGroup
    ) {
        // jezeli ten sam cykl, ale grupa sie dla nich rozni
        const currentGroup = cycleParams[index].temporaryParams.getGroup(
            first(currCycle[EventTypes.INSEMINATION])
        );
        const nextGroup = cycleParams[index + 1].temporaryParams.getGroup(
            first(nextCycle[EventTypes.INSEMINATION])
        );
        if (currentGroup && nextGroup && currentGroup !== nextGroup) {
            const nextInsemination = first(nextCycle[EventTypes.INSEMINATION]);
            return [
                createParamObject(
                    "technologyGroupRemoval",
                    REMOVE_TECHNOLOGY_GROUP_REASONS.INSEMINATION_OTHER_WEEK,
                    cycleParams[index],
                    nextInsemination,
                    ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                    ParamGroups.GENERAL
                ),
            ];
        }
    }
    if (currCycle[EventTypes.PARTURITION].length === 0) {
        const insemination = first(currCycle[EventTypes.INSEMINATION]);
        if (insemination) {
            // wyrzucenie zwierzecia z grupy, poniewaz nie zgloszony byl porod, po okresie opoznienia
            const expectedFarrowingDate = moment
                .utc(insemination.EvTime)
                .startOf("day")
                .add(utilResults.TimeFromInseminationToParturition, "days");
            const diff = expectedFarrowingDate.diff(
                moment.utc().startOf("day"),
                "days"
            );
            if (diff < -utilResults.MaxDelayForBirth) {
                const time = +expectedFarrowingDate
                    .add(utilResults.MaxDelayForBirth, "days")
                    .startOf("day");
                const params = {
                    ...cycleParams[index],
                    dataEv: time,
                    cycleDay: cycleParams[index].temporaryParams?.getCycleDay?.(
                        {EvTime: time}
                    ),
                    groupDay: cycleParams[index].temporaryParams?.getGroupDay?.(
                        {EvTime: time}
                    ),
                    cycleWeek: cycleParams[
                        index
                    ].temporaryParams?.getCycleWeek?.({EvTime: time}),
                };
                return [
                    createParamObject(
                        "technologyGroupRemoval",
                        REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PARTURITION_EVENT,
                        params,
                        null,
                        ParamTypes.TECHNOLOGY_GROUP_REMOVAL,
                        ParamGroups.GENERAL
                    ),
                ];
            }
        }
    } else if (temporaryAnimalData.zeroPigletTimeInCycles?.[index]) {
        return [
            createParamObject(
                "technologyGroupRemoval",
                REMOVE_TECHNOLOGY_GROUP_REASONS.REMOVED_ALL_PIGLETS,
                cycleParams[index],
                null,
                ParamTypes.TECHNOLOGY_GROUP_REMOVAL
            ),
        ];
    }
    return [];
}

function calculateMultiCycleParameters(
    cycleTable,
    cycleParams,
    events,
    utilResults,
    temporaryAnimalData
) {
    const params = [];
    let lastKnownParturition;
    for (let i = 0; i < cycleTable.length; i++) {
        const curr = cycleTable[i];
        const next = cycleTable[i + 1];
        if (next) {
            const separation = last(curr[EventTypes.SEPARATION]);
            const insemination = first(next[EventTypes.INSEMINATION]);
            if (separation && insemination) {
                const diff = moment(insemination.EvTime)
                    .startOf("day")
                    .diff(moment(separation.EvTime).startOf("day"), "days");
                // params.push(createParamObject("separationInseminationDays", diff, cycleParams[i + i], insemination, ParamTypes.DAYS));
                // wg. Daniela informacja ta odnosi się do cylku z inseminacją (styczeń 2024, slack)
                params.push(
                    createParamObject(
                        "separationInseminationDays",
                        diff,
                        cycleParams[i + 1],
                        separation,
                        ParamTypes.DAYS,
                        ParamGroups.SEPARATION
                    )
                );
            }
            let currParturition =
                first(curr[EventTypes.PARTURITION]) || lastKnownParturition; // jezeli w aktualnym cyklu nie bylo porodu to wez ostatnio znany
            let nextParturition = first(next[EventTypes.PARTURITION]);
            if (currParturition && nextParturition) {
                const diff = moment(nextParturition.EvTime)
                    .startOf("day")
                    .diff(
                        moment(currParturition.EvTime).startOf("day"),
                        "days"
                    );
                lastKnownParturition = nextParturition;
                params.push(
                    createParamObject(
                        "timeBetweenParturitions",
                        diff,
                        cycleParams[i],
                        nextParturition,
                        ParamTypes.DAYS,
                        ParamGroups.FARROWING
                    )
                );
            }
        }
        params.push(
            ...calculateTechnologyGroupRemoval(
                curr,
                next,
                i,
                cycleParams,
                events,
                utilResults,
                temporaryAnimalData
            )
        );
    }
    return params;
}

function pushOtherParamsForCycles(cycleResults, cyclesParams) {
    const params = [];
    // const groupedByCycle = Object.values(groupBy(cycleResults, "cycleIndex"));
    // let sumIdleDays = 0;
    // for (let i = 0; i < groupedByCycle.length; i++) {
    //     const curr = groupedByCycle[i];
    //     const next = groupedByCycle[i + 1];
    //     let idleDays =
    //         i === 0
    //             ? curr.reduce((prev, curr) => prev + curr.idleDays, 0)
    //             : +curr[curr.length - 1].idleDays; // dla pierwszego cyklu powinno byc zsumowane
    //     if (next && next.length > 1) {
    //         const dataFromCurrCycle = next.slice(0, -1);
    //         idleDays += dataFromCurrCycle.reduce(
    //             (prev, curr) => prev + curr.idleDays,
    //             0
    //         );
    //     }
    //     // sumIdleDays += idleDays;
    //     // if (cyclesParams[i] && idleDays !== 0) {
    //     //     // params.push(
    //     //     //     createParamObject(
    //     //     //         "idleDays",
    //     //     //         idleDays,
    //     //     //         cyclesParams[i],
    //     //     //         null,
    //     //     //         ParamTypes.AMOUNT,
    //     //     //         ParamGroups.GENERAL
    //     //     //     )
    //     //     // );
    //     // }
    // }
    for (let i = 0; i < cycleResults.length; i++) {
        const {lactationDays, mommyDays} = cycleResults[i];
        if (lactationDays !== null && lactationDays !== 0) {
            params.push(
                createParamObject(
                    "lactationDays",
                    lactationDays,
                    cyclesParams[i],
                    null,
                    ParamTypes.AMOUNT,
                    ParamGroups.GENERAL
                )
            );
        }
        if (mommyDays !== null && mommyDays !== 0) {
            params.push(
                createParamObject(
                    "lactationMommyDays",
                    mommyDays,
                    cyclesParams[i],
                    null,
                    ParamTypes.AMOUNT,
                    ParamGroups.GENERAL
                )
            );
        }
    }

    return params;
}

function averageSeparatedPigletForCycle(cycleTable) {
    let sum = 0;
    const cycles = new Set();
    for (let row of cycleTable) {
        sum += row[EventTypes.SEPARATION].reduce(
            (prev, curr) => prev + curr.EvData.PiCnt,
            0
        );
        cycles.add(row.cycle);
    }
    if (!cycles.size) return 0;
    return sum / cycles.size;
}

function getParamsFromTempData(sharedParameters, temporaryAnimalData) {
    const params = [];
    if (temporaryAnimalData.parturition?.amount > 0) {
        params.push(
            createParamObject(
                "bornPigletWeightAllCycle",
                Math.round(
                    temporaryAnimalData.parturition.weight /
                        temporaryAnimalData.parturition.amount
                ),
                sharedParameters,
                null,
                ParamTypes.WEIGHT,
                ParamGroups.FARROWING
            )
        );
    }
    if (temporaryAnimalData.parturition?.parturitions > 0) {
        params.push(
            createParamObject(
                "liveBornPigletAllCycle",
                temporaryAnimalData.parturition.amount /
                    temporaryAnimalData.parturition.parturitions,
                sharedParameters,
                null,
                ParamTypes.AMOUNT,
                ParamGroups.FARROWING
            )
        );
    }
    if (temporaryAnimalData.separation?.amount > 0) {
        params.push(
            createParamObject(
                "weanedPigletWeightPerAllCycle",
                Math.round(
                    temporaryAnimalData.separation.weight /
                        temporaryAnimalData.separation.amount
                ),
                sharedParameters,
                null,
                ParamTypes.WEIGHT,
                ParamGroups.SEPARATION
            )
        );
    }
    if (temporaryAnimalData.separation?.parturitions > 0) {
        params.push(
            createParamObject(
                "weanedPigletAllCycle",
                temporaryAnimalData.separation.amount /
                    temporaryAnimalData.separation.parturitions,
                sharedParameters,
                null,
                ParamTypes.AMOUNT,
                ParamGroups.SEPARATION
            )
        );
    }
    return params;
}

function getSowState(row, sharedParameters, animal, currentPiglets) {
    const tmpSharedParameters = {
        ...sharedParameters,
        PlcmntID: animal.PlcmntID,
    };
    if (animal.DtaDthTime) {
        const diff = moment().diff(animal.DtaDthTime, "days");
        if (animal.DthRsn === 0) {
            // upadek
            return [
                createParamObject(
                    "sowState",
                    sowState.DEAD,
                    tmpSharedParameters,
                    null,
                    ParamTypes.SOW_STATE,
                    ParamGroups.GENERAL
                ),
                createParamObject(
                    "sowStateDays",
                    diff,
                    tmpSharedParameters,
                    null,
                    ParamTypes.DAYS,
                    ParamGroups.GENERAL
                ),
            ];
        } else {
            // sprzedaz
            return [
                createParamObject(
                    "sowState",
                    sowState.SOLD,
                    tmpSharedParameters,
                    null,
                    ParamTypes.SOW_STATE,
                    ParamGroups.GENERAL
                ),
                createParamObject(
                    "sowStateDays",
                    diff,
                    tmpSharedParameters,
                    null,
                    ParamTypes.DAYS,
                    ParamGroups.GENERAL
                ),
            ];
        }
    }
    const separation = last(row[EventTypes.SEPARATION]);
    if (separation && currentPiglets === 0) {
        const diff = moment().diff(separation.EvTime, "days");
        if (diff > 5) {
            return [
                createParamObject(
                    "sowState",
                    sowState.IDLE,
                    tmpSharedParameters,
                    null,
                    ParamTypes.SOW_STATE,
                    ParamGroups.GENERAL
                ),
                createParamObject(
                    "sowStateDays",
                    diff,
                    tmpSharedParameters,
                    null,
                    ParamTypes.DAYS,
                    ParamGroups.GENERAL
                ),
            ];
        }
        return [
            createParamObject(
                "sowState",
                sowState.SEPARATED,
                tmpSharedParameters,
                null,
                ParamTypes.SOW_STATE,
                ParamGroups.GENERAL
            ),
            createParamObject(
                "sowStateDays",
                diff,
                tmpSharedParameters,
                null,
                ParamTypes.DAYS,
                ParamGroups.GENERAL
            ),
        ];
    }
    const parturition = first(row[EventTypes.PARTURITION]);
    if (parturition) {
        const diff = moment().diff(parturition.EvTime, "days");
        return [
            createParamObject(
                "sowState",
                sowState.FARROWED,
                tmpSharedParameters,
                null,
                ParamTypes.SOW_STATE,
                ParamGroups.GENERAL
            ),
            createParamObject(
                "sowStateDays",
                diff,
                tmpSharedParameters,
                null,
                ParamTypes.DAYS,
                ParamGroups.GENERAL
            ),
        ];
    }
    const insemination = first(row[EventTypes.INSEMINATION]);
    if (insemination) {
        const diff = moment.utc().diff(insemination.EvTime, "days");
        return [
            createParamObject(
                "sowState",
                sowState.INSEMINATED,
                tmpSharedParameters,
                null,
                ParamTypes.SOW_STATE,
                ParamGroups.GENERAL
            ),
            createParamObject(
                "sowStateDays",
                diff,
                tmpSharedParameters,
                null,
                ParamTypes.DAYS,
                ParamGroups.GENERAL
            ),
        ];
    }
    if (!animal.DtaInTime) return [];
    const diff = moment().diff(animal.DtaInTime, "days");
    return [
        createParamObject(
            "sowState",
            sowState.NOT_IN_PRODUCTION,
            tmpSharedParameters,
            null,
            ParamTypes.SOW_STATE,
            ParamGroups.GENERAL
        ),
        createParamObject(
            "sowStateDays",
            diff,
            tmpSharedParameters,
            null,
            ParamTypes.DAYS,
            ParamGroups.GENERAL
        ),
    ];
}

function calculateLastCycle(
    animal,
    cycleTable,
    row,
    sharedParameters,
    cyclesParams,
    utilResults,
    temporaryAnimalData,
    currentPiglets
) {
    const params = [];
    const lastEvent = getLastEvent(row);
    if (lastEvent) {
        params.push(
            createParamObject(
                "timeLastAction",
                lastEvent.EvTime,
                sharedParameters,
                lastEvent,
                ParamTypes.TIME,
                ParamGroups.GENERAL
            )
        );
    }
    const {cycleResults, results} = getParturitionIndexForASow(
        animal,
        cycleTable,
        utilResults,
        animal.DtaDthTime ? moment(animal.DtaDthTime) : undefined
    );
    params.push(
        createParamObject(
            "index",
            results.parturitionIndex,
            sharedParameters,
            null,
            ParamTypes.INDEX,
            ParamGroups.GENERAL
        )
    );
    params.push(...pushOtherParamsForCycles(cycleResults, cyclesParams));
    params.push(
        ...calculateLastCycleParametersRelatedToInsemination(
            cycleTable,
            sharedParameters,
            utilResults
        )
    );
    params.push(
        createParamObject(
            "weanedPerYear",
            results.parturitionIndex *
                averageSeparatedPigletForCycle(cycleTable),
            sharedParameters,
            null,
            ParamTypes.AMOUNT,
            ParamGroups.SEPARATION
        )
    );
    params.push(
        ...getParamsFromTempData(
            cycleTable,
            sharedParameters,
            temporaryAnimalData
        )
    );
    params.push(...getSowState(row, sharedParameters, animal, currentPiglets));
    params.push(
        createParamObject(
            "currentLocation",
            animal.PlcmntID,
            {...sharedParameters, PlcmntID: animal.PlcmntID},
            null,
            ParamTypes.LOCATION,
            ParamGroups.GENERAL
        )
    );
    return params;
}

function calculateParturitionDurationParameter(cycle, sharedParameters) {
    const params = [];
    const start = cycle[EventTypes.PARTURITION_START][0];
    const end = cycle[EventTypes.PARTURITION_END][0];
    if (start && end) {
        const diff = end.EvTime - start.EvTime;
        params.push(
            createParamObject(
                "durationParturition",
                diff,
                sharedParameters,
                null,
                ParamTypes.DAYS,
                ParamGroups.FARROWING
            )
        );
    }
    return params;
}
function calculateP2Parameters(cycle, sharedParameters) {
    const params = [];
    for (let event of cycle[EventTypes.FAT_MEASUREMENT] || []) {
        params.push(
            createParamObject(
                "fatMeasurement",
                event?.EvData?.Fat,
                sharedParameters,
                event,
                ParamTypes.AMOUNT,
                ParamGroups.FARROWING
            )
        );
    }
    return params;
}

function calculateParametersRelatedToGilts(cycle, sharedParameters) {
    let separatedGiltsCount = 0;
    for (let tattoo of cycle[EventTypes.TATTOO]) {
        separatedGiltsCount += get(tattoo, "EvData.TattooedAnimals", []).filter(
            (item) => !!item.SeparationTime
        ).length;
    }
    for (let separatedGilts of cycle[EventTypes.SEPARATION_TO_MOMMY_GILTS]) {
        if (separatedGilts.EvData.SeparationTime) {
            separatedGiltsCount += 1;
        }
    }
    if (separatedGiltsCount === 0) return [];
    return [
        createParamObject(
            "weanedGilts",
            separatedGiltsCount,
            sharedParameters,
            null,
            ParamTypes.AMOUNT,
            ParamGroups.SEPARATION
        ),
    ];
}

function calculateParametersRelatedToFostering(cycle, sharedParameters) {
    const params = [];
    for (let event of cycle[EventTypes.MOMMY]) {
        params.push(
            createParamObject(
                "fostering",
                event.EvData.PiCnt,
                sharedParameters,
                event,
                ParamTypes.AMOUNT,
                ParamGroups.FARROWING
            )
        );
    }
    for (let event of cycle[EventTypes.SEPARATION_TO_MOMMY]) {
        params.push(
            createParamObject(
                "fostering",
                -event.EvData.PiCnt,
                sharedParameters,
                event,
                ParamTypes.AMOUNT,
                ParamGroups.FARROWING
            )
        );
    }
    for (let event of cycle[EventTypes.TATTOO]) {
        const animals =
            event.EvData.TattooedAnimals?.filter((item) => !!item.MommyTime)
                ?.length || 0;
        if (animals !== 0) {
            params.push(
                createParamObject(
                    "fostering",
                    -animals,
                    sharedParameters,
                    event,
                    ParamTypes.AMOUNT,
                    ParamGroups.FARROWING
                )
            );
        }
    }
    for (let event of cycle[EventTypes.SEPARATION_TO_MOMMY_GILTS]) {
        if (!event.EvData.MommyTime) {
            params.push(
                createParamObject(
                    "fostering",
                    event.AnmCnt,
                    sharedParameters,
                    event,
                    ParamTypes.AMOUNT,
                    ParamGroups.FARROWING
                )
            );
        }
    }
    return params;
}

function calculateParametersRelatedToPigStatus(
    cycle,
    sharedParameters,
    animal
) {
    const params = [];
    const events = [
        ...cycle[EventTypes.PARTURITION],
        ...cycle[EventTypes.FALL_PIGLETS],
        ...cycle[EventTypes.SEPARATION_TO_MOMMY],
        ...cycle[EventTypes.SEPARATION_TO_MOMMY_GILTS],
        ...cycle[EventTypes.MOMMY],
        ...cycle[EventTypes.TATTOO],
        ...cycle[EventTypes.SEPARATION],
    ].sort((a, b) => a.EvTime - b.EvTime);
    let currentPiglets = 0;
    let lastEv = null;
    for (let event of events) {
        switch (event.EvCode) {
            case EventTypes.FALL_PIGLETS:
                currentPiglets -= event.EvData.Piglets;
                break;
            case EventTypes.PARTURITION:
                currentPiglets += event.EvData.HealthyCnt;
                break;
            case EventTypes.SEPARATION_TO_MOMMY:
                currentPiglets -= event.EvData.PiCnt;
                break;
            case EventTypes.MOMMY:
                currentPiglets += event.EvData.PiCnt;
                break;
            case EventTypes.SEPARATION:
                currentPiglets -= event.EvData.PiCnt;
                break;
            case EventTypes.SEPARATION_TO_MOMMY_GILTS:
                if (!event.EvData.MommyTime) {
                    currentPiglets += 1;
                }
                break;
            case EventTypes.TATTOO:
                currentPiglets -=
                    event.EvData.TattooedAnimals?.filter(
                        (item) => !!item.MommyTime
                    )?.length || 0;
                break;
            default:
                console.log(event);
        }
        if (currentPiglets === 0) {
            lastEv = event;
        }
    }
    if (currentPiglets === 0 && lastEv !== null) {
        const endDate =
            animal.DtaDthTime || animal.DtaDelTime
                ? moment(animal.DtaDthTime || animal.DtaDelTime)
                : moment();
        const diff = endDate
            .startOf("day")
            .diff(moment(lastEv.EvTime).startOf("day"), "days");
        params.push(
            createParamObject(
                "zeroPigletsDays",
                diff,
                sharedParameters,
                lastEv,
                ParamTypes.DAYS,
                ParamGroups.FARROWING
            )
        );
    }
    return {
        params,
        currentPiglets,
        zeroPigletsTime: currentPiglets === 0 && lastEv ? lastEv.EvTime : null,
    };
}

function createNextTransferParam(
    destinationID,
    time,
    event,
    buildingsMap,
    utilResults,
    isMommy,
    sharedParameters
) {
    const destination = buildingsMap.get(destinationID);
    if (!destination) return [];
    if (
        [SectorType.DELIVERY, SectorType.MATING, SectorType.SOWS].includes(
            destination.SType
        )
    ) {
        const timeOnLocation =
            destination.SType === SectorType.MATING
                ? utilResults.TimeOnMating
                : destination.SType === SectorType.SOWS
                  ? utilResults.TimeOnSows
                  : isMommy
                    ? utilResults.TimeOnBirthRoomMommy
                    : utilResults.TimeOnBirthRoom;
        const expectedTransferDate = moment(time)
            .startOf("day")
            .add(timeOnLocation, "days");
        const diff = expectedTransferDate.diff(moment().startOf("day"), "days");
        return [
            createParamObject(
                "nextTransfer",
                diff,
                sharedParameters,
                event,
                ParamTypes.DAYS,
                ParamGroups.GENERAL
            ),
        ];
    }
    return [];
}

function calculateTransferParams(
    animal,
    events,
    buildingsMap,
    utilResults,
    isMommy,
    sharedParameters
) {
    const params = [];
    for (let i = events.length - 1; i >= 0; i--) {
        const event = events[i];
        if (event.EvCode === EventTypes.TRANSFER) {
            params.push(
                ...createNextTransferParam(
                    event.EvData.DstID,
                    event.EvTime,
                    event,
                    buildingsMap,
                    utilResults,
                    isMommy,
                    sharedParameters
                )
            );
            break;
        }
    }
    if (params.length === 0) {
        params.push(
            ...createNextTransferParam(
                animal.PlcmntID,
                animal.DtaInTime,
                null,
                buildingsMap,
                utilResults,
                isMommy,
                sharedParameters
            )
        );
    }
    if (!animal.DtaDthTime && !animal.DtaDelTime) {
        const currentLocation = buildingsMap.get(animal.PlcmntID);
        if (currentLocation?.hasOwnProperty("SType")) {
            params.push(
                createParamObject(
                    "currentSector",
                    currentLocation.SType,
                    sharedParameters,
                    null,
                    ParamTypes.SECTOR_TYPE,
                    ParamGroups.GENERAL
                )
            );
        }
    }
    return params;
}

function calculateParametersRelatedToIsMommy(cycle, sharedParameters) {
    let isMommy = true;
    const params = [];
    for (let event of cycle[EventTypes.IS_MOMMY]) {
        params.push(
            createParamObject(
                "isMommy",
                true,
                sharedParameters,
                event,
                ParamTypes.BOOLEAN,
                ParamGroups.GENERAL
            )
        );
    }
    if (params.length === 0) {
        isMommy = false;
        params.push(
            createParamObject(
                "isMommy",
                false,
                sharedParameters,
                null,
                ParamTypes.BOOLEAN,
                ParamGroups.GENERAL
            )
        );
    }
    return {
        isMommy,
        params,
    };
}

function getCycleParams(event, cycleParams, animal) {
    const cycle = cycleParams.find(
        (item) =>
            item.temporaryParams &&
            item.temporaryParams.cycleDates.start <= event.EvTime &&
            item.temporaryParams.cycleDates.end >= event.EvTime
    );
    if (cycle) return cycle;
    const lastCycle = last(cycleParams);
    if (
        lastCycle?.temporaryParams &&
        lastCycle.temporaryParams.cycleDates.start <= event.EvTime
    )
        return lastCycle;
    return {
        AnmID: animal.AnmID,
        AnmNo1: animal.AnmNo1,
        isPresent: !animal.DtaDelTime && !animal.DtaDthTime,
    };
}

function calculateVaccinationParameters(events, cyclesParams, animal) {
    let params = [];
    const vaccinations = (events || []).filter(
        (item) =>
            item.EvCode === EventTypes.GRAFTING ||
            item.EvCode === EventTypes.PIGLETS_TREATMENT
    );
    for (let row of vaccinations) {
        const cycleParams = getCycleParams(row, cyclesParams, animal);
        params.push(
            createParamObject(
                row.EvCode === EventTypes.PIGLETS_TREATMENT
                    ? "pigletVaccination"
                    : "vaccination",
                row.EvData.Medicine,
                cycleParams,
                row,
                ParamTypes.MEDICINE,
                ParamGroups.GENERAL
            )
        );
    }
    return params;
}

function calculateFirstCycleParams(cycle, sharedParameters, animal) {
    const params = [];
    const insemination = first(cycle[EventTypes.INSEMINATION]);
    if (insemination) {
        if (animal.DtaBrthTime) {
            const age = moment(insemination.EvTime).diff(
                moment(animal.DtaBrthTime),
                "days"
            );
            params.push(
                createParamObject(
                    "firstInseminationAge",
                    age,
                    sharedParameters,
                    insemination,
                    ParamTypes.DAYS,
                    ParamGroups.REPRODUCTION
                )
            );
        }
        if (animal.DtaInTime) {
            const daysAfterInsertion = moment(insemination.EvTime).diff(
                animal.DtaInTime,
                "days"
            );
            params.push(
                createParamObject(
                    "timeFromInsertionToInsemination",
                    daysAfterInsertion,
                    sharedParameters,
                    insemination,
                    ParamTypes.DAYS,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    return params;
}

function calculateParametersForCycle(
    row,
    index,
    cyclesParams,
    animal,
    events,
    cycleTable,
    utilResults,
    buildingsMap,
    temporaryAnimalData,
    crossBreedingDictionary,
    pedigreeDictionary
) {
    const cycleParams = cyclesParams[index];
    const {isMommy, params: mommyParams} = calculateParametersRelatedToIsMommy(
        row,
        cycleParams
    );
    const {
        params: pigStatusParams,
        currentPiglets,
        zeroPigletsTime,
    } = calculateParametersRelatedToPigStatus(row, cycleParams, animal);
    temporaryAnimalData.zeroPigletTimeInCycles.push(zeroPigletsTime);
    const params = [
        ...mommyParams,
        ...calculateParametersRelatedToActiveNipples(row, cycleParams),
        ...calculateParametersRelatedToInsemination(row, cycleParams, animal),
        ...calculateParametersRelatedToParturition(
            row,
            cycleParams,
            utilResults,
            temporaryAnimalData,
            cycleParams.actCycle,
            crossBreedingDictionary,
            pedigreeDictionary
        ),
        ...calculateParametersRelatedToFallPiglets(row, cycleParams),
        ...calculateParametersRelatedToCastration(
            row,
            cycleParams,
            temporaryAnimalData
        ),
        ...calculateParametersRelatedToUSG(row, cycleParams),
        ...calculateParturitionDurationParameter(row, cycleParams),

        ...calculateParametersRelatedToGilts(row, cycleParams),
        ...calculateParametersRelatedToFostering(row, cycleParams),
        ...calculateParametersRelatedToSeparation(
            row,
            cycleParams,
            buildingsMap,
            temporaryAnimalData,
            isMommy
        ),
        ...pigStatusParams,
        ...calculateP2Parameters(row, cycleParams),
    ];
    if (cycleParams.actCycle) {
        params.push(
            ...calculateLastCycle(
                animal,
                cycleTable,
                row,
                cycleParams,
                cyclesParams,
                utilResults,
                temporaryAnimalData,
                currentPiglets
            )
        );
        params.push(
            ...calculateTransferParams(
                animal,
                events,
                buildingsMap,
                utilResults,
                isMommy,
                cycleParams
            )
        );
        params.push(
            createParamObject(
                "sowPiglets",
                currentPiglets,
                {...cycleParams, dataEv: +new Date()},
                null,
                ParamTypes.AMOUNT,
                ParamGroups.REPRODUCTION
            )
        );
    }
    if (index === 0) {
        params.push(...calculateFirstCycleParams(row, cycleParams, animal));
    }
    return params;
}

function calculateInsertionParams(events, cycleParams, groups) {
    if (!events) return [];
    const insertion = events.find(
        (item) => item.EvCode === EventTypes.INSERTION
    );
    if (!insertion) {
        return [];
    }
    if (insertion.EvData?.AnimalKind !== AnimalTypes.SOW) {
        if (giltWasInGroup(groups, insertion.AnmID, events, insertion.EvTime))
            return []; // zwierze bylo w grupie wiec doda sie gdzie indziej
        return [
            createParamObject(
                "giltInsertedCnt",
                insertion.AnmCnt,
                cycleParams[0],
                insertion,
                ParamTypes.AMOUNT
            ),
        ];
    }
    return [
        createParamObject(
            "sowInsertion",
            insertion.EvTime,
            cycleParams[0],
            insertion,
            ParamTypes.TIME,
            ParamGroups.GENERAL
        ),
    ];
}

function calculateRaceParameters(animal, sharedParameters, pedigreeDictionary) {
    const params = [];
    if (animal.Race) {
        params.push(
            createParamObject(
                "race",
                animal.Race,
                sharedParameters,
                null,
                ParamTypes.RACE,
                ParamGroups.GENERAL
            )
        );
    }
    if (animal.AnmNo2 || animal.Genetics?.TattooNumber) {
        const tattoo = animal.AnmNo2 || animal.Genetics?.TattooNumber;
        if (tattoo !== "-") {
            // fajna wartość na bazie jest zapisana
            params.push(
                createParamObject(
                    "tattooNumber",
                    tattoo,
                    sharedParameters,
                    null,
                    ParamTypes.TATTOO,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    const sow = pedigreeDictionary.sows[animal.Genetics?.SowID];
    if (sow) {
        params.push(
            createParamObject(
                "mother",
                sow.AnmID,
                sharedParameters,
                null,
                ParamTypes.ANIMAL,
                ParamGroups.REPRODUCTION
            )
        );
        if (sow.AnmNo2) {
            params.push(
                createParamObject(
                    "motherTattoo",
                    sow.AnmNo2,
                    sharedParameters,
                    null,
                    ParamTypes.TATTOO,
                    ParamGroups.REPRODUCTION
                )
            );
        }
        if (sow.Race) {
            params.push(
                createParamObject(
                    "motherRace",
                    sow.Race,
                    sharedParameters,
                    null,
                    ParamTypes.RACE,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    const boar = pedigreeDictionary.boars[animal.Genetics?.BoarID];
    if (boar) {
        params.push(
            createParamObject(
                "father",
                boar.AnmID,
                sharedParameters,
                null,
                ParamTypes.ANIMAL,
                ParamGroups.REPRODUCTION
            )
        );
        if (boar.AnmNo2) {
            params.push(
                createParamObject(
                    "fatherTattoo",
                    boar.AnmNo2,
                    sharedParameters,
                    null,
                    ParamTypes.TATTOO,
                    ParamGroups.REPRODUCTION
                )
            );
        }
        if (boar.Race) {
            params.push(
                createParamObject(
                    "fatherRace",
                    boar.Race,
                    sharedParameters,
                    null,
                    ParamTypes.RACE,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    return params;
}

function calculateIdleDays(
    cycleTable,
    cycleParams,
    temporaryAnimalData,
    animal
) {
    const params = [];
    let sumIdleDays = 0;
    const groupedCycles = Map.groupBy(cycleTable, ({cycle}) => cycle); // mapa, bo potrzebne sa klucze jako liczby
    for (let [key, value] of [...groupedCycles.entries()]) {
        let idleDays = 0;
        const firstCycleInGroup = first(value);
        const lastCycleInGroup = last(value);
        // jezeli pierwszy i ostatni cykl sa rozne, to znaczyy ze mamy doczynienia z powtorka
        // dodajemy wtedy dni pomiedzy pierwsza inseminacja z pierwszego cyklu i pierwsza inseminacja z ostatniego cyklu
        if (firstCycleInGroup !== lastCycleInGroup) {
            const firstCycleTime =
                first(firstCycleInGroup[EventTypes.INSEMINATION])?.EvTime ||
                firstCycleInGroup.StartTime;
            const lastCycleTime =
                first(lastCycleInGroup[EventTypes.INSEMINATION])?.EvTime ||
                lastCycleInGroup.StartTime;
            idleDays += moment(lastCycleTime)
                .startOf("day")
                .diff(moment(firstCycleTime).startOf("day"), "days");
        }
        // wyliczanie roznicy dni od odsadzenia/meblowania ostatnich prosiat do kolejnej inseminacji
        // jezeli nie ma kolejnej inseminacji to bierzemy aktualny dzien, chyba ze zwierze zostalo sprzedane lub padlo
        const indexOfLastCycle = cycleTable.indexOf(lastCycleInGroup);
        const timeWhenRemovedAllPiglets =
            temporaryAnimalData.zeroPigletTimeInCycles[indexOfLastCycle];
        if (timeWhenRemovedAllPiglets) {
            const nextCycle = first(groupedCycles.get(key + 1));
            const nextCycleTime = nextCycle
                ? first(nextCycle[EventTypes.INSEMINATION])?.EvTime ||
                  nextCycle.StartTime
                : animal.DtaDthTime || animal.DtaDelTime || +new Date();
            idleDays += moment(nextCycleTime)
                .startOf("day")
                .diff(
                    moment(timeWhenRemovedAllPiglets).startOf("dayy"),
                    "days"
                );
        }
        if (idleDays !== 0) {
            sumIdleDays += idleDays;
            params.push(
                createParamObject(
                    "idleDays",
                    idleDays,
                    cycleParams[indexOfLastCycle],
                    null,
                    ParamTypes.AMOUNT,
                    ParamGroups.REPRODUCTION
                )
            );
        }
    }
    if (sumIdleDays > 0) {
        params.push(
            createParamObject(
                "idleDaysAllCycle",
                sumIdleDays,
                cycleParams[cycleParams.length - 1],
                null,
                ParamTypes.AMOUNT,
                ParamGroups.REPRODUCTION
            )
        );
    }
    return params;
}

function calculateAnimalObjectParameters(animal, sharedParameters) {
    const params = [];
    if (animal.DtaInTime) {
        params.push(
            createParamObject(
                "insertionTime",
                animal.DtaInTime,
                sharedParameters,
                null,
                ParamTypes.TIME,
                ParamGroups.GENERAL
            )
        );
    }
    if (animal.HerdNumber) {
        params.push(
            createParamObject(
                "herd",
                animal.HerdNumber,
                sharedParameters,
                null,
                ParamTypes.HERD_NUMBER,
                ParamGroups.GENERAL
            )
        );
    }
    if (animal.SupplierID) {
        params.push(
            createParamObject(
                "supplier",
                animal.SupplierID,
                sharedParameters,
                null,
                ParamTypes.SUPPLIER,
                ParamGroups.GENERAL
            )
        );
    }
    return params;
}

function makeGetCycleDay(insemination) {
    if (!insemination) return null;
    const inseminationInFarmTime = moment(
        moment(insemination.EvTime).format(moment.HTML5_FMT.DATE)
    );
    return function getCycleDay(event) {
        if (!event) return undefined;
        return moment(event.EvTime)
            .startOf("day")
            .diff(inseminationInFarmTime, "days");
    };
}

function makeGetGroup(groups) {
    return function getGroupNumber(event) {
        if (!event) return groups[0];
        const group = groups.find(
            (item) => event.EvTime >= item.from && event.EvTime < item.to
        );
        if (!group) return groups[0];
        return group;
    };
}

export function makeGetCycleWeek(insemination) {
    if (!insemination) return null;
    const inseminationInFarmTime = moment(
        moment(insemination.EvTime).format(moment.HTML5_FMT.DATE)
    );
    return function getCycleDay(event) {
        if (!event) return undefined;
        return moment(event.EvTime)
            .startOf("day")
            .diff(inseminationInFarmTime, "weeks");
    };
}

function createAnimalLocationTime(animal, events) {
    if (!events) return null;
    const insertion = events.find(
        (item) => item.EvCode === EventTypes.INSERTION
    );
    const transfers = events.filter(
        (item) => item.EvCode === EventTypes.TRANSFER
    );
    if (!insertion) return null;
    const locationHistory = [];
    let currentLocation = {
        PlcmntID: insertion.EvData.PlcmntID,
        start: insertion.EvTime,
        end: Infinity,
        AnmID: animal.AnmID,
    };
    for (let transfer of transfers) {
        locationHistory.push({
            ...currentLocation,
            end: transfer.EvTime - 1,
        });
        currentLocation = {
            PlcmntID: transfer.EvData.DstID,
            start: transfer.EvTime,
            end: Infinity,
            AnmID: animal.AnmID,
        };
    }
    locationHistory.push({
        ...currentLocation,
        end: animal.DtaDelTime || animal.DtaDthTime || Infinity,
    });
    return locationHistory;
}

function makeGetPlcmntID(animalLocationTimes) {
    if (!animalLocationTimes) return null;
    const index = new Flatbush(animalLocationTimes.length);
    for (let row of animalLocationTimes) {
        index.add(row.start, 0, row.end, 0);
    }
    index.finish();
    function getPlcmntID(event) {
        if (!event) return undefined;
        const found = index.search(event.EvTime, 0, event.EvTime, 0);
        if (found.length === 0) return undefined;
        return animalLocationTimes[found[0]].PlcmntID;
    }
    return memoize(getPlcmntID);
}

export function getDictionaryID(event) {
    if (!event) return undefined;
    switch (event.EvCode) {
        case EventTypes.FALL:
        case EventTypes.FALL_PIGLETS:
            return `FALL_${event.EvData.Reasn}`;
        case EventTypes.NO_PREGNANCY:
            return `NOPREGNANCY_${event.EvData.Reasn}`;
        default:
            return undefined;
    }
}

export function calculateAnimalParameters(
    animal,
    events,
    cycleTable,
    utilResults,
    buildingsMap,
    groups,
    pedigreeDictionary,
    crossBreedingDictionary
) {
    const params = [];

    const technologyGroupStart = utilResults.TechnologyGroupStart;
    const technologyGroupWeeks = utilResults.TechnologyGroupWeeks;

    const cyclesParams = [];

    const temporaryAnimalData = {
        zeroPigletTimeInCycles: [],
    };
    const animalLocationTimes = createAnimalLocationTime(animal, events);
    const getPlcmntID = makeGetPlcmntID(animalLocationTimes);

    let wasInseminated = false;

    for (let i = 0; i < cycleTable.length; i++) {
        const row = cycleTable[i];
        const actCycle = i === cycleTable.length - 1;
        const groups = getTechnologyGroupNumber(row, {
            technologyGroupStart,
            technologyGroupWeeks,
        });
        cyclesParams[i] = {
            cycle: row.cycle,
            cycleIndex: i,
            actCycle,
            AnmID: animal.AnmID,
            AnmNo1: animal.AnmNo1,
            isPresent: !animal.DtaDelTime && !animal.DtaDthTime,
            race: animal.Race,
            AnimalKind: animal.AnimalKind,
            RFID: animal.RFID,
            temporaryParams: {
                getCycleDay: makeGetCycleDay(
                    first(row[EventTypes.INSEMINATION])
                ),
                getCycleWeek: makeGetCycleWeek(
                    first(row[EventTypes.INSEMINATION])
                ),
                getPlcmntID,
                getDictionaryID,
                cycleDates: {start: row.StartTime, end: row.EndTime},
            },
        };
        if (groups.length > 0) {
            // cyclesParams[i].group_nr = groupNumber.name;
            cyclesParams[i].temporaryParams.getGroup = makeGetGroup(groups);
        }
        params.push(
            ...calculateParametersForCycle(
                row,
                i,
                cyclesParams,
                animal,
                events,
                cycleTable,
                utilResults,
                buildingsMap,
                temporaryAnimalData,
                crossBreedingDictionary,
                pedigreeDictionary
            )
        );
        if (groups.length > 0) wasInseminated = true;
    }
    if (cyclesParams.length === 0) {
        // sytuacja z tych mało prawdopodobnych, ale prawdziwa
        // console.log("animalId=%s [%s] has no cycle", animal?.AnmID, animal?.AnmNo1);
        cyclesParams.push({
            cycle: 0, // jezeli nie miala zadnego cyklu to przyjmujemy numer 0 - loszka
            actCycle: true,
            AnmID: animal.AnmID,
            AnmNo1: animal.AnmNo1,
            isPresent: !animal.DtaDelTime && !animal.DtaDthTime,
            cycleIndex: 0,
            group_nr: "UNKNOWN",
            AnimalKind: animal.AnimalKind,
            RFID: animal.RFID,
        });
        params.push(...getSowState({}, cyclesParams[0], animal));
    }
    params.push(
        ...calculateParametersRelatedToRepetition(cycleTable, cyclesParams)
    );
    params.push(...calculateInsertionParams(events, cyclesParams, groups));
    params.push(
        ...calculateMultiCycleParameters(
            cycleTable,
            cyclesParams,
            events,
            utilResults,
            temporaryAnimalData
        )
    );
    params.push(
        ...calculateVaccinationParameters(events, cyclesParams, animal)
    );
    params.push(
        ...calculateParametersRelatedToFallOrSellOfAnimal(
            animal,
            events,
            last(cyclesParams),
            groups
        )
    );
    params.push(
        ...calculateParametersRelatedToAnimalAge(animal, last(cyclesParams))
    );
    params.push(
        ...calculateRaceParameters(
            animal,
            last(cyclesParams),
            pedigreeDictionary
        )
    );
    params.push(...calculateAnimalObjectParameters(animal, last(cyclesParams)));
    params.push(
        ...calculateIdleDays(
            cycleTable,
            cyclesParams,
            temporaryAnimalData,
            animal
        )
    );
    return {
        params,
        wasInseminated,
        animalLocationTimes: animalLocationTimes || [],
        currentCycle: last(cycleTable)?.cycle || 0,
    };
}

export function calculateTechnologyGroupParametersForAnimal(
    animal,
    events,
    cycleTable,
    utilResults,
    buildingsMap
) {
    const params = [];

    const technologyGroupStart = utilResults.TechnologyGroupStart;
    const technologyGroupWeeks = utilResults.TechnologyGroupWeeks;

    const cyclesParams = [];

    for (let i = 0; i < cycleTable.length; i++) {
        const row = cycleTable[i];
        const actCycle = i === cycleTable.length - 1;
        const groups = getTechnologyGroupNumber(row, {
            technologyGroupStart,
            technologyGroupWeeks,
        });
        cyclesParams[i] = {
            cycle: row.cycle,
            actCycle,
            AnmID: animal.AnmID,
            AnmNo1: animal.AnmNo1,
            isPresent: !animal.DtaDelTime && !animal.DtaDthTime,
            cycleIndex: i,
            AnimalKind: animal.AnimalKind,
        };
        if (groups.length > 0) {
            // cyclesParams[i].group_nr = groupNumber.name;
            cyclesParams[i].temporaryParams = {
                // groupStart: groupNumber.start,
                getCycleDay: makeGetCycleDay(
                    first(row[EventTypes.INSEMINATION])
                ),
                getGroup: makeGetGroup(groups),
                cycleDates: {start: row.StartTime, end: row.EndTime},
            };
            const {isMommy, params: isMommyParams} =
                calculateParametersRelatedToIsMommy(row, cyclesParams[i]);
            const {params: zeroPigletDaysParams} =
                calculateParametersRelatedToPigStatus(
                    row,
                    cyclesParams[i],
                    animal
                );
            params.push(
                ...calculateParametersRelatedToInsemination(
                    row,
                    cyclesParams[i],
                    utilResults
                ),
                ...calculateTechnologyGroupRemoval(
                    row,
                    cycleTable[i + 1],
                    i,
                    cyclesParams,
                    events,
                    utilResults
                ),
                ...calculateParametersRelatedToUSG(row, cyclesParams[i]),
                ...calculateParametersRelatedToParturition(
                    row,
                    cyclesParams[i],
                    utilResults
                ),
                ...calculateParametersRelatedToSeparation(
                    row,
                    cyclesParams[i],
                    buildingsMap,
                    {},
                    isMommy
                ),
                ...isMommyParams,
                ...zeroPigletDaysParams
            );
            if (cyclesParams[i] === actCycle) {
                params.push(
                    ...calculateLastCycleParametersRelatedToInsemination(
                        cycleTable,
                        cyclesParams[i],
                        utilResults
                    )
                );
            }
        }
    }
    if (cyclesParams.length === 0) {
        // sytuacja z tych mało prawdopodobnych, ale prawdziwa
        // console.log("animalId=%s [%s] has no cycle", animal?.AnmID, animal?.AnmNo1);
        cyclesParams.push({
            cycle: 0, // jezeli nie miala zadnego cyklu to przyjmujemy numer 0 - loszka
            actCycle: true,
            AnmID: animal.AnmID,
            AnmNo1: animal.AnmNo1,
            isPresent: !animal.DtaDelTime && !animal.DtaDthTime,
            cycleIndex: 0,
            AnimalKind: animal.AnimalKind,
        });
    }
    params.push(
        ...calculateVaccinationParameters(events, cyclesParams, animal)
    );
    return params;
}

export const ForTests = {
    createParamObject,
    getTechnologyGroupNumber,
    calculateParametersRelatedToParturition,
    calculateParametersRelatedToFallPiglets,
    calculateParametersRelatedToSeparation,
    calculateParametersRelatedToCastration,
    calculateParametersRelatedToActiveNipples,
    calculateParametersRelatedToInsemination,
    calculateParametersRelatedToFallOrSellOfAnimal,
    calculateParametersRelatedToUSG,
    calculateParametersRelatedToAnimalAge,
    calculateParametersRelatedToRepetition,
    calculateParametersRelatedToGilts,
    calculateLastCycle,
    calculateParturitionDurationParameter,
    pushOtherParamsForCycles,
    averageSeparatedPigletForCycle,
    getLastEvent,
    calculateMultiCycleParameters,
    calculateLastCycleParametersRelatedToInsemination,
    calculateParametersRelatedToPigStatus,
    calculateTransferParams,
    getParamsFromTempData,
    getSowState,
    calculateInsertionParams,
    createAnimalLocationTime,
    makeGetPlcmntID,
    getDictionaryID,
    calculateVaccinationParameters,
    calculateFirstCycleParams,
    calculateP2Parameters,
    getCycleParams,
    calculateIdleDays,
};
